import { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Checkbox, Typography, IconButton, TextField, Collapse } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import LaunchIcon from '@mui/icons-material/Launch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useGetPromptNamesQuery } from '@tymely/services';

import { styles } from './TemplateGuidelines.styles';

const BASE_GUIDELINES = [
    {
        label: 'Global Guideline',
        value: 'RESPONSE_GENERATION_GLOBAL_GUIDELINES',
    },
    {
        label: 'Retailer Guideline',
        value: 'RESPONSE_GENERATION_RETAILER_GUIDELINES',
    },
    {
        label: 'Ticket Information',
        value: 'TICKET_INFORMATION_PROMPT',
    },
    {
        label: 'Intent Information',
        value: 'INTENT_INFORMATION_PROMPT',
    },
];

type Guideline = {
    label: string;
    value: string;
};

type TemplateGuidelinesProps = {
    template: string | null;
    onCodeChange: (template: string) => void;
};

const TemplateGuidelines = ({ template, onCodeChange }: TemplateGuidelinesProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [additionalGuidelines, setAdditionalGuidelines] = useState<Guideline[]>([...BASE_GUIDELINES]);
    const [showAdditional, setShowAdditional] = useState(false);
    const { data: promptNames = [], isLoading } = useGetPromptNamesQuery();
    const hasProcessedInitial = useRef(false);

    useEffect(() => {
        if (!template || isLoading) return;

        if (!hasProcessedInitial.current) {
            hasProcessedInitial.current = true;

            const promptRegex = /{{([^}]+)}}/g;
            const matches = Array.from(template.matchAll(promptRegex));

            matches.forEach((match) => {
                const promptName = match[1].trim();
                const isBaseGuideline = BASE_GUIDELINES.some((g) => g.value === promptName);
                const isAdditionalGuideline = additionalGuidelines.some((g) => g.value === promptName);
                const isValidPrompt = promptNames.includes(promptName);

                if (!isBaseGuideline && !isAdditionalGuideline && isValidPrompt) {
                    setAdditionalGuidelines((prev) => [...prev, { label: promptName, value: promptName }]);
                }
            });
        }
    }, [template, promptNames, isLoading]);

    const filteredPromptNames = useMemo(() => {
        if (!searchQuery) return promptNames;
        return promptNames.filter((name) => name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [promptNames, searchQuery]);

    const handleGuidelineToggle = (promptName: string) => {
        const isInAdditional = additionalGuidelines.some((g) => g.value === promptName);

        if (isInAdditional) {
            setAdditionalGuidelines((prev) => prev.filter((g) => g.value !== promptName));
        } else {
            setAdditionalGuidelines((prev) => [...prev, { label: promptName, value: promptName }]);
        }
    };

    return (
        <>
            <Typography variant="h5">Settings</Typography>
            <Box sx={styles.container}>
                {additionalGuidelines.map((guideline) => {
                    const guidelineRegexp = new RegExp(`{{\\s*${guideline.value}\\s*}}`, 'g');
                    const isChecked = guidelineRegexp.test(template || '');

                    return (
                        <Box key={guideline.value} sx={styles.guidelineItem}>
                            <Checkbox
                                size="large"
                                checked={isChecked}
                                icon={<AddCircleOutlineIcon />}
                                checkedIcon={<CheckIcon />}
                                onChange={() => {
                                    const newTemplate = isChecked
                                        ? template?.replace(guidelineRegexp, '')
                                        : `${template}\n{{${guideline.value}}}`;
                                    onCodeChange(newTemplate || '');
                                }}
                                sx={styles.checkbox}
                            />
                            <span>{guideline.label}</span>
                            <IconButton
                                size="small"
                                href={'https://app.tymelyinsights.com/prompt-editor?prompt=' + guideline.value}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={styles.launchButton}
                            >
                                <LaunchIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    );
                })}

                <Box sx={styles.additionalHeader} onClick={() => setShowAdditional(!showAdditional)}>
                    <Typography variant="h6">Additional Guidelines</Typography>
                    <ExpandMoreIcon sx={styles.expandIcon(showAdditional)} />
                </Box>

                <Collapse in={showAdditional}>
                    <Box sx={styles.container}>
                        <TextField
                            size="small"
                            placeholder="Search guidelines..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            fullWidth
                            sx={styles.searchField}
                        />
                        <Box sx={styles.scrollableList}>
                            {isLoading ? (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    Loading...
                                </Typography>
                            ) : filteredPromptNames.length === 0 ? (
                                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                    No guidelines found
                                </Typography>
                            ) : (
                                filteredPromptNames.map((promptName) => {
                                    const isSelected = additionalGuidelines.some((g) => g.value === promptName);
                                    return (
                                        <Box
                                            key={promptName}
                                            sx={styles.promptItem}
                                            onClick={() => handleGuidelineToggle(promptName)}
                                        >
                                            <Checkbox
                                                size="large"
                                                checked={isSelected}
                                                icon={<AddCircleOutlineIcon />}
                                                checkedIcon={<CheckIcon />}
                                                sx={styles.checkbox}
                                            />
                                            <span>{promptName}</span>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        </>
    );
};

export default TemplateGuidelines;
